import type { CheckboxProps, TooltipProps } from '@mui/material';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import type { DefaultTheme } from '@mui/styles';
import { makeStyles } from '@mui/styles';

import color from 'color';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SimpleTooltip from '../../../../../../components/SimpleTooltip';
import { Show } from '../../../../../../components/Show';

export const InfoIcon = ({
  description,
  tooltipProps,
}: {
  description?: React.ReactNode;
  tooltipProps?: Partial<TooltipProps>;
}) => {
  if (description) {
    return (
      <SimpleTooltip
        title={description}
        style={{ marginLeft: 0, display: 'inline' }}
        arrow
        {...tooltipProps}
      >
        <InfoOutlinedIcon
          sx={{
            pointerEvents: 'auto',
            marginBottom: '-0.3rem',
            marginLeft: '0.35rem',
            marginRight: 0,
            fontSize: '1.2rem',
            cursor: 'default',
          }}
        />
      </SimpleTooltip>
    );
  }

  return null;
};

interface LabelProp {
  label?: React.ReactNode;
  description?: string;
  checkboxProps?: CheckboxProps;
  rulesAndFactors?: React.ReactNode;
}

interface DisabledProp {
  disabled?: boolean;
}

const Mandatory = ({
  label,
  description,
  rulesAndFactors = null,
}: LabelProp) => {
  const classes = useClasses({ isMandatory: true });

  const formControlLabelClasses = useFormControlClasses({});

  const labelContent =
    typeof label === 'string' ? (
      <SimpleTooltip title={label} enterDelay={500}>
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {label}
        </Box>
      </SimpleTooltip>
    ) : (
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label ?? 'mandatory'}
      </Box>
    );

  return (
    <Box
      sx={{
        display: 'flex',
        maxWidth: '100%',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <FormControlLabel
        classes={formControlLabelClasses}
        control={
          <Checkbox
            checked
            classes={classes}
            color="secondary"
            sx={{ pointerEvents: 'none' }}
          />
        }
        label={
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {labelContent}
            <Box
              component="span"
              sx={{ flexShrink: 0, ml: '0.25rem', mr: '0.75rem' }}
            >
              <InfoIcon description={description} />
            </Box>
            <Show when={Boolean(rulesAndFactors)}>
              <Box
                component="span"
                sx={{ pointerEvents: 'auto', flexShrink: 0 }}
              >
                {rulesAndFactors}
              </Box>
            </Show>
          </Box>
        }
        sx={{
          margin: 0,
          maxWidth: '100%',
          width: '100%',
          overflow: 'hidden',
          '& .MuiFormControlLabel-label': {
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
          },
        }}
      />
    </Box>
  );
};

const Selected = ({
  label,
  disabled,
  description,
  checkboxProps = {},
  rulesAndFactors = null,
}: LabelProp & DisabledProp) => {
  const classes = useClasses({});
  const formControlLabelClasses = useFormControlClasses({});

  const labelContent =
    typeof label === 'string' ? (
      <SimpleTooltip title={label} enterDelay={500}>
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {label}
        </Box>
      </SimpleTooltip>
    ) : (
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label ?? 'selected'}
      </Box>
    );

  return (
    <Box
      sx={{
        pointerEvents: disabled ? 'none' : 'auto',
        display: 'flex',
        maxWidth: '100%',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <FormControlLabel
        disabled={disabled}
        classes={formControlLabelClasses}
        control={
          <Checkbox
            color="secondary"
            checked
            disabled={disabled}
            classes={classes}
            {...checkboxProps}
          />
        }
        label={
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {labelContent}
            <Box
              component="span"
              sx={{ flexShrink: 0, ml: '0.25rem', mr: '0.75rem' }}
            >
              <InfoIcon description={description} />
            </Box>
            {rulesAndFactors && (
              <Box component="span" sx={{ flexShrink: 0 }}>
                {rulesAndFactors}
              </Box>
            )}
          </Box>
        }
        sx={{
          margin: 0,
          maxWidth: '100%',
          width: '100%',
          overflow: 'hidden',
          '& .MuiFormControlLabel-label': {
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
          },
        }}
      />
    </Box>
  );
};

const Available = ({
  label,
  disabled,
  description,
  checkboxProps = {},
  rulesAndFactors = null,
}: LabelProp & DisabledProp) => {
  const classes = useClasses({});
  const formControlLabelClasses = useFormControlClasses({});

  const labelContent =
    typeof label === 'string' ? (
      <SimpleTooltip title={label} enterDelay={500}>
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {label}
        </Box>
      </SimpleTooltip>
    ) : (
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label ?? 'available'}
      </Box>
    );

  return (
    <Box
      sx={{
        pointerEvents: disabled ? 'none' : 'auto',
        display: 'flex',
        maxWidth: '100%',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <FormControlLabel
        disabled={disabled}
        classes={formControlLabelClasses}
        control={
          <Checkbox
            classes={classes}
            color="secondary"
            disabled={disabled}
            {...checkboxProps}
          />
        }
        label={
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {labelContent}
            <Box
              component="span"
              sx={{ flexShrink: 0, ml: '0.25rem', mr: '0.75rem' }}
            >
              <InfoIcon description={description} />
            </Box>
            {rulesAndFactors && (
              <Box component="span" sx={{ flexShrink: 0 }}>
                {rulesAndFactors}
              </Box>
            )}
          </Box>
        }
        sx={{
          margin: 0,
          maxWidth: '100%',
          width: '100%',
          overflow: 'hidden',
          '& .MuiFormControlLabel-label': {
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
          },
        }}
      />
    </Box>
  );
};

const NotAvailable = ({
  label,
  description,
  checkboxProps = {},
  rulesAndFactors = null,
}: LabelProp) => {
  const classes = useClasses({});
  const formControlLabelClasses = useFormControlClasses({});

  const labelContent =
    typeof label === 'string' ? (
      <SimpleTooltip title={label} enterDelay={500}>
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {label}
        </Box>
      </SimpleTooltip>
    ) : (
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label || 'not available'}
      </Box>
    );

  return (
    <Box
      sx={{
        pointerEvents: 'none',
        display: 'flex',
        maxWidth: '100%',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <FormControlLabel
        disabled
        classes={formControlLabelClasses}
        control={
          <Checkbox
            color="primary"
            classes={classes}
            checked={false}
            {...checkboxProps}
          />
        }
        label={
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {labelContent}
            <Box
              component="span"
              sx={{ flexShrink: 0, ml: '0.25rem', mr: '0.75rem' }}
            >
              <InfoIcon description={description} />
            </Box>
            {rulesAndFactors && (
              <Box component="span" sx={{ flexShrink: 0 }}>
                {rulesAndFactors}
              </Box>
            )}
          </Box>
        }
        sx={{
          margin: 0,
          maxWidth: '100%',
          width: '100%',
          overflow: 'hidden',
          '& .MuiFormControlLabel-label': {
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
          },
        }}
      />
    </Box>
  );
};

export default {
  Mandatory,
  Selected,
  Available,
  NotAvailable,
} as const;

type UseClassesProps = { isMandatory?: boolean };

const useClasses = makeStyles<DefaultTheme, UseClassesProps>(({ palette }) => {
  return {
    root: {
      padding: '0 0.5rem 0 0.75rem',
      color: (props) =>
        getCheckboxColor({
          palette,
          isMandatory: props.isMandatory,
        }) as string,

      '&.Mui-checked': {
        color: (props: UseClassesProps) =>
          getCheckboxColor({
            palette,
            isMandatory: props.isMandatory,
          }) as string,
      },
    },
  };
});

const getCheckboxColor = ({
  palette,
  isMandatory,
}: {
  palette: any;
  isMandatory: boolean | undefined;
}) => {
  if (isMandatory && palette.mode === 'light') {
    return color(palette.primary.main).darken(0.2).toString();
  }
  if (isMandatory && palette.mode === 'dark') {
    return palette.primary.contrastText;
  }
  return palette.primary.cowbellBlue;
};

type UseFormControlClassesProps = { disabled?: boolean };
const useFormControlClasses = makeStyles<
  DefaultTheme & UseFormControlClassesProps
>(({ palette, disabled }) => ({
  label: {
    color: disabled ? palette.text.disabled : palette.primary.main,
    textTransform: 'capitalize',
  },
}));
